'use client';

import { Suspense, useEffect, useState, useCallback } from 'react';
import { useDiscover, type DiscoverPreset, type ContentType } from './use-discover';
import { DiscoverFilterBar } from './discover-filter-panel';
import { DiscoverFilterSidebar } from './discover-filter-sidebar';
import { DiscoverFeatured } from './discover-featured';
import { DiscoverResults, MultiTypeResults } from './discover-results';
import { cn } from '@/lib/utils';

interface DiscoverHubProps {
  preset?: DiscoverPreset;
}

function DiscoverHubInner({ preset }: DiscoverHubProps) {
  const {
    filters,
    updateFilter,
    toggleType,
    toggleTag,
    toggleChannel,
    toggleScope,
    clearFilter,
    clearAllFilters,
    resetFilters,
    activeFilterCount,
    presetTags,
    presetChannels,
    viewMode,
    setViewMode,
    isFeaturedMode,
    isMultiTypeMode,
    featured,
    featuredLoading,
    featuredError,
    retryFeatured,
    loadMoreFeatured,
    sectionLoading,
    results,
    loading,
    hasMore,
    loadMore,
    multiSections,
  } = useDiscover(preset);

  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);

  useEffect(() => {
    const handler = () => resetFilters();
    window.addEventListener('discoverReset', handler);
    return () => window.removeEventListener('discoverReset', handler);
  }, [resetFilters]);

  const handleSeeAll = useCallback((type: ContentType) => {
    toggleType(type);
    const mainScroll = document.getElementById('main-scroll');
    if (mainScroll) mainScroll.scrollTo({ top: 0, behavior: 'smooth' });
  }, [toggleType]);

  const effType = filters.types.length === 1 ? filters.types[0] : 'all';

  return (
    <div className="@container space-y-4">
      {preset?.title && (
        <div>
          <h1 className="text-xl font-bold">{preset.title}</h1>
          {preset.subtitle && (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{preset.subtitle}</p>
          )}
        </div>
      )}

      <DiscoverFilterBar
        filters={filters}
        viewMode={viewMode}
        activeFilterCount={activeFilterCount}
        onQueryChange={(q) => updateFilter('query', q)}
        onToggleType={toggleType}
        onToggleTag={toggleTag}
        onToggleChannel={toggleChannel}
        onToggleScope={toggleScope}
        onClearFilter={clearFilter}
        onClearAll={clearAllFilters}
        onViewModeChange={setViewMode}
        onOpenFilters={() => setFilterSidebarOpen(true)}
      />

      {/* Content area with crossfade transition */}
      <div className="relative">
        <div
          className={cn(
            'transition-opacity duration-300',
            isFeaturedMode ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none',
          )}
        >
          {isFeaturedMode && (
            <DiscoverFeatured
              data={featured}
              loading={featuredLoading}
              error={featuredError}
              onRetry={retryFeatured}
              viewMode={viewMode}
              onSeeAll={handleSeeAll}
              onToggleTag={toggleTag}
              onLoadMore={loadMoreFeatured}
              sectionLoading={sectionLoading}
            />
          )}
        </div>

        <div
          className={cn(
            'transition-opacity duration-300',
            !isFeaturedMode ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none',
          )}
        >
          {!isFeaturedMode && (
            isMultiTypeMode ? (
              <MultiTypeResults
                sections={multiSections}
                viewMode={viewMode}
              />
            ) : (
              <DiscoverResults
                type={effType}
                items={results}
                viewMode={viewMode}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            )
          )}
        </div>
      </div>

      {/* Filter sidebar */}
      <DiscoverFilterSidebar
        open={filterSidebarOpen}
        onClose={() => setFilterSidebarOpen(false)}
        filters={filters}
        activeFilterCount={activeFilterCount}
        allowedTypes={preset?.allowedTypes}
        presetTags={presetTags}
        presetChannels={presetChannels}
        onUpdateFilter={updateFilter}
        onToggleType={toggleType}
        onToggleTag={toggleTag}
        onToggleChannel={toggleChannel}
        onToggleScope={toggleScope}
        onClearFilter={clearFilter}
        onClearAll={clearAllFilters}
        onSortChange={(s) => updateFilter('sort', s)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    </div>
  );
}

export function DiscoverHub({ preset }: DiscoverHubProps) {
  const presetKey = preset?.title || 'default';

  return (
    <Suspense fallback={<div className="animate-pulse space-y-4 @container">
      <div className="h-10 bg-muted rounded-xl" />
      <div className="grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-muted rounded-xl" />)}
      </div>
    </div>}>
      <DiscoverHubInner key={presetKey} preset={preset} />
    </Suspense>
  );
}
